import React, { useState, useEffect, useMemo } from "react";
import { useAppContext } from "../../App.jsx";
import {
  json_to_base64,
  base64_to_json,
  nils,
  getv,
  cdelay,
} from "../../utils/utils.jsx";
import { useAuthContext } from "@/wrappers/AuthWrapper.jsx";
import { useAccountContext } from "@/wrappers/AccountWrapper.jsx";
import {
  q_profile_info,
  q_skins_master_applied,
  q_skins_master_cc,
  q_skins_master_instore,
  q_skins_master_owned,
} from "@/queries/queries.jsx";
import { useQueries } from "@tanstack/react-query";
import { twMerge } from "tailwind-merge";
import { Helmet } from "react-helmet-async";
import { useProfileData } from "@/hooks/useProfileData.jsx";
import { useVaultBalances } from "./useVaultBalances.jsx";
import { useVaultCoresInf } from "@/hooks/useVaultCoresInf.jsx";
import { ProfileRow } from "./ProfileRow.jsx";
import { PrerequisitesRows } from "./PrerequisitesRows.jsx";
import { MySkins } from "./MySkins.jsx";
// import { Applicator } from "./Applicator.jsx";
import { Store } from "./Store.jsx";
import { Ledger } from "./Ledger.jsx";

export const SkinsMasterContext = React.createContext({});
export const useSkinsMasterContext = () => React.useContext(SkinsMasterContext);

export const SkinsMaster = () => {
  const appcon = useAppContext();
  const { psearch, upd_psearch, usd_to_tok_val } = appcon;
  const aucon = useAuthContext();
  const accon = useAccountContext();
  const [tab, set_tab] = useState(psearch.tab ?? "my_skins"); // Default tab
  const [skmglob, set_skmglob] = useState(
    psearch.skmglob ? base64_to_json(psearch.skmglob) : {},
  );
  const [prereq, set_prereq] = useState({});

  const vault = useMemo(() => {
    if (!nils(psearch.v)) return psearch.v.toLowerCase();
    return aucon.vault ?? null;
  }, [psearch.v, aucon.vault, aucon.auth]);

  const isauvault = useMemo(() => {
    if (nils(vault)) return false;
    return aucon.vault == vault && aucon.auth === true;
  }, [aucon.vaut, aucon.auth, vault]);

  const exisvault = !nils(vault);

  const _profile = useProfileData(vault);
  const _bal = useVaultBalances(vault);
  const _vault_cores = useVaultCoresInf(vault, exisvault);
  const cores_map = _vault_cores.hmap ?? {};
  // useEffect(() => { console.log("_vault_cores.hmap", _vault_cores.hmap); }, [_vault_cores]);

  useEffect(() => {
    upd_psearch({
      tab,
      skmglob: json_to_base64(skmglob),
    });
  }, [tab, skmglob]);

  const [qo_profile] = useQueries({
    queries: [q_profile_info({ vault }, { enabled: exisvault })],
  });
  const profile = useMemo(() => {
    return getv(qo_profile, "data.result");
  }, [qo_profile.dataUpdatedAt]);

  const full_access = isauvault;

  const ciparams = useMemo(() => {
    if (!full_access) return null;
    if (!aucon.auth || !aucon.aumode || !aucon.vault) return null;
    return aucon.get_contract_init_params();
  }, [aucon.aumode, aucon.vault, aucon.auth]);

  const [qo_skins_owned, qo_skin_instore] = useQueries({
    queries: [
      q_skins_master_owned({ vault }, { enabled: exisvault }),
      q_skins_master_instore({ vault }, { enabled: true }),
    ],
  });

  const [owned_skins, skin_infomap] = useMemo(() => {
    let d = getv(qo_skins_owned, "data.result");
    let skins = getv(d, "skins", []);
    let infomap = getv(d, "infomap", {});
    return [skins, infomap];
  }, [qo_skins_owned.dataUpdatedAt]);

  const [qo_skins_applied] = useQueries({
    queries: [
      q_skins_master_applied(
        { rvsids: owned_skins.map((s) => s.rvsid) },
        { enabled: exisvault && owned_skins.length > 0 },
      ),
    ],
  });

  const [applied_map, hmap] = useMemo(() => {
    let d = getv(qo_skins_applied, "data.result");
    let applied_map = getv(d, "applied_map", {});
    let hmap = getv(d, "hmap", {});
    return [applied_map, hmap];
  }, [qo_skins_applied.dataUpdatedAt]);

  const [store_indis, store_packs] = useMemo(() => {
    let d = getv(qo_skin_instore, "data.result");
    let indis = getv(d, "indis", []);
    let packs = getv(d, "packs", []);
    return [indis, packs];
  }, [qo_skin_instore.dataUpdatedAt]);

  // Post-burn function to refresh all relevant data
  const post_burn_function = async () => {
    try {
      await q_skins_master_cc({
        k: "vault_skins",
        vault,
      }).queryFn();
      await cdelay(2 * 1e3);
      // Refetch owned skins data
      qo_skins_owned.refetch();

      // Refetch applied skins data
      qo_skins_applied.refetch();

      // Refetch vault balances (SGC rewards)
      if (_bal.refetch) {
        _bal.refetch();
      }

      // console.log("Post-burn data refresh initiated");
    } catch (error) {
      console.warn("Failed to refresh data after burn:", error);
    }
  };

  // Post-purchase function to refresh all relevant data
  const post_purchase_function = async () => {
    try {
      await q_skins_master_cc({
        k: "vault_skins",
        vault,
      }).queryFn();
      await cdelay(2 * 1e3);
      // Refetch owned skins data (new skins added to inventory)
      qo_skins_owned.refetch();

      // Refetch store data (may affect availability)
      qo_skin_instore.refetch();

      // Refetch vault balances (tokens spent)
      if (_bal.refetch) {
        _bal.refetch();
      }

      // console.log("Post-purchase data refresh initiated");
    } catch (error) {
      console.warn("Failed to refresh data after purchase:", error);
    }
  };

  // useEffect(() => { console.log("vault changed", { vault, isauvault, full_access, }); }, [vault, isauvault, full_access]);

  const skinsmaster_con = {
    tab,
    set_tab,

    skmglob,
    set_skmglob,

    prereq,
    set_prereq,

    vault,
    isauvault,
    full_access,

    qo_profile,
    profile,

    qo_skins_owned,
    owned_skins,
    skin_infomap,

    qo_skins_applied,
    applied_map,
    hmap,

    qo_skin_instore,
    store_indis,
    store_packs,

    _profile,
    _bal,
    _vault_cores,

    usd_to_tok_val,
    ciparams,

    cores_map,

    post_burn_function,
    post_purchase_function,
  };

  return (
    <SkinsMasterContext.Provider value={skinsmaster_con}>
      <Helmet>
        <title>Skins Master || DNA Racing</title>
      </Helmet>
      <div className="h-page relative">
        {/* SkinsMaster content will be added here */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="h-8 md:h-12"></div>

          <div className="text-center text-white mb-12 md:mb-16">
            <h1 className="text-3xl font-digi">Skins Master</h1>
          </div>

          {/* Profile Row */}
          <ProfileRow />

          {/* Prerequisites Rows */}
          <PrerequisitesRows />

          {/* Tab Navigation */}
          <div className="flex justify-center items-center mb-8 sm:mb-10 md:mb-12 lg:mb-16">
            <div className="flex p-1 sm:p-1.5 rounded-full space-x-1 bg-r3dark/20 border border-r3lig/30 backdrop-blur-md">
              {[
                { key: "my_skins", label: "My Skins" },
                // { key: "applicator", label: "Applicator" },
                { key: "store", label: "Store" },
                { key: "ledger", label: "Ledger" },
              ].map((tabItem) => (
                <button
                  key={tabItem.key}
                  onClick={() => set_tab(tabItem.key)}
                  className={twMerge(
                    "px-3 sm:px-4 md:px-5 lg:px-6 py-2 sm:py-2.5 md:py-3 rounded-full font-digi font-semibold text-xs sm:text-sm md:text-base transition-all duration-300 relative overflow-hidden group",
                    tab === tabItem.key
                      ? "bg-gradient-to-r from-r3reg to-r3dark text-white shadow-xl shadow-r3reg/60 transform scale-105 hover:shadow-2xl hover:shadow-r3reg/80"
                      : "text-gray-300 hover:text-white hover:bg-gradient-to-r hover:from-r3lig/20 hover:to-r3reg/20 hover:scale-105 hover:shadow-lg hover:shadow-r3lig/40 active:scale-95",
                  )}
                >
                  <span className="relative z-10">{tabItem.label}</span>
                  {tab === tabItem.key && (
                    <div className="absolute inset-0 bg-gradient-to-r from-r3lig/30 to-r3reg/30 animate-glow-pulse rounded-full" />
                  )}
                  {tab !== tabItem.key && (
                    <div className="absolute inset-0 bg-gradient-to-r from-r3lig/20 to-r3reg/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="relative overflow-hidden min-h-[600px]">
            {tab === "my_skins" && <MySkins />}
            {/*             {tab === "applicator" && <Applicator />} */}
            {tab === "store" && <Store />}
            {tab === "ledger" && <Ledger />}
          </div>

          <div className="h-16 md:h-24"></div>
        </div>
      </div>
    </SkinsMasterContext.Provider>
  );
};
